import { Box, FormControl, FormHelperText } from "@mui/material";
import { useEffect, useState } from "react";
import { EditorContent, useEditorState } from "@tiptap/react";
import useTipTapEditor from "./useTipTapEditor";
import type { EditorProps } from "./types";
import WordCountMessage from "./WordCountMessage";
import Toolbar from "./Toolbar";
import { wordLimitMessage } from "../../../formatting/defaults";

function FormatableTextBox({
  id,
  label,
  initialValue,
  helperText,
  errorText,
  maxWords,
  onBlur,
  onChange,
  error = false,
  disabled = false,
}: EditorProps) {
  const [hasEditorChanged, setHasEditorChanged] = useState(false);
  const editor = useTipTapEditor({
    id,
    label,
    initialValue,
    onBlur,
    onUpdate: (value, wordCount) => {
      setHasEditorChanged(true);
      onChange?.(value, wordCount);
    },
    disabled,
  });
  const count =
    useEditorState({
      editor,
      selector: ({ editor: currentEditor }) =>
        currentEditor?.storage.characterCount.words() ?? 0,
    }) ?? 0;
  const isOverWordLimit = Boolean(
    hasEditorChanged && maxWords && count > maxWords,
  );
  const hasError = error || isOverWordLimit;
  const displayedErrorText =
    isOverWordLimit && maxWords ? wordLimitMessage(maxWords) : errorText;
  const errorMessageId =
    hasError && displayedErrorText ? `${id}-error-text` : undefined;

  useEffect(() => {
    if (!editor) return;
    const editorElement = editor.view.dom;
    if (hasError) editorElement.setAttribute("aria-invalid", "true");
    else editorElement.removeAttribute("aria-invalid");

    if (hasError && errorMessageId)
      editorElement.setAttribute("aria-errormessage", errorMessageId);
    else editorElement.removeAttribute("aria-errormessage");
  }, [editor, errorMessageId, hasError]);

  return (
    <FormControl fullWidth disabled={disabled} error={hasError}>
      <Box
        sx={{
          mt: 1,
          border: 1,
          borderColor: hasError ? "error.main" : "divider",
          borderRadius: 1.5,
          overflow: "hidden",
          bgcolor: disabled ? "action.disabledBackground" : "background.paper",
          transition: (theme) =>
            theme.transitions.create(["border-color", "box-shadow"], {
              duration: theme.transitions.duration.shortest,
            }),
          "&:hover": {
            borderColor: hasError ? "error.main" : "text.secondary",
          },
          "&:focus-within": {
            borderColor: hasError ? "error.main" : "brand.earthly",
            boxShadow: (theme) =>
              `0 0 0 3px ${hasError ? theme.palette.error.main : theme.palette.primary.main}20`,
          },
          "& .tiptap": {
            minHeight: 160,
            px: 1.5,
            py: 1.25,
            outline: "none",
          },
        }}
      >
        <Toolbar editor={editor} disabled={disabled} />
        <EditorContent editor={editor} />
      </Box>
      {helperText ? (
        <FormHelperText
          error={false}
          id={`${id}-helper-text`}
          sx={{ mx: 0, mt: 0.75, color: "text.secondary" }}
        >
          {helperText}
        </FormHelperText>
      ) : null}
      {hasError && displayedErrorText ? (
        <FormHelperText error sx={{ mx: 0, mt: 0.75 }} id={errorMessageId}>
          {displayedErrorText}
        </FormHelperText>
      ) : null}
      <WordCountMessage count={count} maxWords={maxWords} error={hasError} />
    </FormControl>
  );
}

export default FormatableTextBox;
